import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Package, ShieldCheck, Scissors, PackageCheck, Truck } from "lucide-react";

/** Big-Button mobile view for floor staff — thumb-friendly production controls. */
const NEXT = {
  material_received: { label: "Start QC 1", stage: "qc1", icon: ShieldCheck, tone: "#3D5A80" },
  qc1: { label: "Send to Customization", stage: "customization", icon: Scissors, tone: "#C45A41" },
  customization: { label: "Start QC 2", stage: "qc2", icon: ShieldCheck, tone: "#3D5A80" },
  qc2: { label: "Start Packing", stage: "packing", icon: PackageCheck, tone: "#D99036" },
  packing: { label: "Mark Dispatched", stage: "dispatched", icon: Truck, tone: "#4A7258" },
};

const STAGE_LABEL = {
  procurement: "Awaiting material",
  material_received: "Material in",
  qc1: "In QC 1",
  customization: "In customization",
  qc2: "In QC 2",
  packing: "Being packed",
  dispatched: "Dispatched",
  delivered: "Delivered",
};

export default function FloorFloor() {
  const [orders, setOrders] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/orders");
    setOrders(data.filter(o => !["dispatched", "delivered"].includes(o.stage)));
  };
  useEffect(() => { load(); }, []);

  const advance = async (id, stage) => {
    try { await api.post(`/orders/${id}/stage`, { stage }); toast.success(`Moved to ${STAGE_LABEL[stage]}`); load(); }
    catch(e){ toast.error(e.response?.data?.detail || "Blocked"); }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="label-tiny mb-1">Floor</div>
        <h1 className="font-display text-3xl tracking-tight">Production Queue</h1>
        <p className="text-sm text-[#757169] mt-1">Tap the big button to move each order forward.</p>
      </div>

      {orders.length === 0 && (
        <div className="card-surface p-10 text-center text-[#757169]">
          <Package size={40} className="mx-auto mb-3 opacity-40" />
          <div className="font-display font-semibold">Floor is clear.</div>
          <div className="text-sm">No orders in production right now.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {orders.map(o => {
          const nxt = NEXT[o.stage];
          const Icon = nxt?.icon || Package;
          return (
            <div key={o.id} className="card-surface p-5 fade-up" data-testid={`floor-card-${o.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="mono text-sm font-semibold">{o.order_no}</div>
                  <div className="text-[#757169] text-sm">{o.customer_name}</div>
                </div>
                <span className="badge badge-warn">{STAGE_LABEL[o.stage] || o.stage}</span>
              </div>
              <div className="text-xs text-[#757169] mb-4">
                {o.items?.length || 0} items · <span className="mono">{(o.items||[]).reduce((a,b)=>a+(b.qty||0),0)}</span> pieces
              </div>
              {nxt ? (
                <button
                  onClick={()=>advance(o.id, nxt.stage)}
                  data-testid={`floor-advance-${o.id}`}
                  className="w-full py-5 rounded-lg text-white font-display font-bold text-lg flex items-center justify-center gap-3 transition-transform active:scale-95"
                  style={{ background: nxt.tone }}
                >
                  <Icon size={22}/> {nxt.label}
                </button>
              ) : (
                <button
                  onClick={()=>nav(`/orders/${o.id}`)}
                  className="w-full py-5 rounded-lg border-2 border-dashed border-[#EAE5DB] text-[#757169] font-display font-semibold"
                >
                  Open order for next step →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
